import { grpcClientFactory } from './grpcClientFactory';
function protoAuthorToTs(protoAuthor) {
    if (!protoAuthor)
        return null;
    return {
        userId: protoAuthor.getUserId() || '',
        email: protoAuthor.getEmail() || '',
        firstName: protoAuthor.getFirstName() || '',
        lastName: protoAuthor.getLastName() || '',
        commentColor: protoAuthor.getCommentColor() || '',
        avatarUrl: protoAuthor.getAvatarUrl() || ''
    };
}
function protoAttachmentToTs(protoAttachment) {
    return {
        id: protoAttachment.getId() || '',
        filename: protoAttachment.getFilename() || '',
        contentType: protoAttachment.getContentType() || '',
        sizeBytes: protoAttachment.getSizeBytes() || 0,
        presignedUrl: protoAttachment.getPresignedUrl() || ''
    };
}
function protoNoteToTs(protoNote) {
    return {
        id: protoNote.getId() || '',
        artifactId: protoNote.getArtifactId() || '',
        projectId: protoNote.getProjectId() || '',
        content: protoNote.getContent() || '',
        author: protoAuthorToTs(protoNote.getAuthor()),
        canEdit: protoNote.getCanEdit() || false,
        createdAt: protoNote.getCreatedAt() || 0,
        updatedAt: protoNote.getUpdatedAt() || 0,
        attachments: (protoNote.getAttachmentsList() || []).map(protoAttachmentToTs)
    };
}
class ArtifactNoteService {
    constructor() {
        this.client = grpcClientFactory.createUserProjectsClient({
            withConditionalAuth: true,
            enableCompression: true
        });
    }
    clearClient() {
        this.client = grpcClientFactory.createUserProjectsClient({
            withConditionalAuth: true,
            enableCompression: true
        });
    }
    async createNote(artifactId, content) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.CreateArtifactNoteRequest;
                const grpcReq = new Req();
                grpcReq.setUserArtifactId(artifactId);
                grpcReq.setContent(content);
                this.client.createArtifactNote(grpcReq, {}, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to create note: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve({
                        success: resp.getSuccess(),
                        message: resp.getMessage(),
                        noteId: resp.getNoteId()
                    });
                });
            }
            catch (e) {
                reject(new Error(`Failed to create note: ${e}`));
            }
        });
    }
    async listNotes(artifactId) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.ListArtifactNotesRequest;
                const grpcReq = new Req();
                grpcReq.setUserArtifactId(artifactId);
                this.client.listArtifactNotes(grpcReq, {}, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to list notes: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve((resp.getNotesList() || []).map(protoNoteToTs));
                });
            }
            catch (e) {
                reject(new Error(`Failed to list notes: ${e}`));
            }
        });
    }
    async updateNote(noteId, content) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.UpdateArtifactNoteRequest;
                const grpcReq = new Req();
                grpcReq.setNoteId(noteId);
                grpcReq.setContent(content);
                this.client.updateArtifactNote(grpcReq, {}, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to update note: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve({ success: resp.getSuccess(), message: resp.getMessage() });
                });
            }
            catch (e) {
                reject(new Error(`Failed to update note: ${e}`));
            }
        });
    }
    async deleteNote(noteId) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.DeleteArtifactNoteRequest;
                const grpcReq = new Req();
                grpcReq.setNoteId(noteId);
                this.client.deleteArtifactNote(grpcReq, {}, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to delete note: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve({ success: resp.getSuccess(), message: resp.getMessage() });
                });
            }
            catch (e) {
                reject(new Error(`Failed to delete note: ${e}`));
            }
        });
    }
    async getAttachmentUploadURL(noteId, filename, contentType, sizeBytes) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.GetNoteAttachmentUploadURLRequest;
                const grpcReq = new Req();
                grpcReq.setNoteId(noteId);
                grpcReq.setFilename(filename);
                grpcReq.setContentType(contentType);
                grpcReq.setSizeBytes(sizeBytes);
                this.client.getNoteAttachmentUploadURL(grpcReq, {}, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to get attachment upload URL: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve({
                        attachmentId: resp.getAttachmentId(),
                        presignedUrl: resp.getPresignedUrl()
                    });
                });
            }
            catch (e) {
                reject(new Error(`Failed to get attachment upload URL: ${e}`));
            }
        });
    }
    async confirmAttachment(attachmentId) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.ConfirmNoteAttachmentRequest;
                const grpcReq = new Req();
                grpcReq.setAttachmentId(attachmentId);
                this.client.confirmNoteAttachment(grpcReq, {}, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to confirm attachment: ${err.message}`));
                        return;
                    }
                    resolve();
                });
            }
            catch (e) {
                reject(new Error(`Failed to confirm attachment: ${e}`));
            }
        });
    }
    async deleteAttachment(attachmentId) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.DeleteNoteAttachmentRequest;
                const grpcReq = new Req();
                grpcReq.setAttachmentId(attachmentId);
                this.client.deleteNoteAttachment(grpcReq, {}, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to delete attachment: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve({ success: resp.getSuccess(), message: resp.getMessage() });
                });
            }
            catch (e) {
                reject(new Error(`Failed to delete attachment: ${e}`));
            }
        });
    }
    // Presigned PUT then confirm, matching commentService.uploadAttachment.
    async uploadAttachment(noteId, file, onProgress) {
        const uploadUrlResponse = await this.getAttachmentUploadURL(noteId, file.name, file.type || 'application/octet-stream', file.size);
        await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable && onProgress) {
                    onProgress(Math.round((e.loaded / e.total) * 100));
                }
            };
            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve();
                }
                else {
                    reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
                }
            };
            xhr.onerror = () => {
                reject(new Error('Network error during upload'));
            };
            xhr.open('PUT', uploadUrlResponse.presignedUrl);
            xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
            xhr.send(file);
        });
        await this.confirmAttachment(uploadUrlResponse.attachmentId);
    }
}
export const artifactNoteService = new ArtifactNoteService();
